/**
 * Everything inside the React Flow provider: the version chrome, the canvas,
 * and the modals that act on a version.
 *
 * Its own file because the tab around it is the part that talks to the store —
 * what to read, when to save, what publishing and restoring mean. This is what
 * that produces on screen.
 */

import React, { FC, useMemo, useRef, useState } from 'react';
import { AOPDraft, AOPDraftPlan, AOPScenario, AOPVersionSummary } from '../../types/AOPTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import AOPBuilderCanvas from './AOPBuilderCanvas';
import AOPPublishBlockedModal, { blockedItemsOf } from './AOPPublishBlockedModal';
import AOPVersionBar, { AOPSaveStatus } from './AOPVersionBar';
import AOPVersionPill from './AOPVersionPill';
import { useFillViewportHeight } from './useFillViewportHeight';
import { AOP_VERSION_CHROME, versionModeOf } from './utils/AOPVersionUtils';
import './AOPBuilder.less';

export interface AOPProcedureBodyProps {
	draft: AOPDraft;
	shownDraft: AOPDraft;
	summaries: AOPVersionSummary[];
	versionCount?: number;
	isLoadingMoreVersions: boolean;
	onLoadMoreVersions: () => void;
	selectedVersion: number | null;
	publishedVersion: number | null;
	hasUnpublishedChanges: boolean | null;
	isReadOnly: boolean;
	/**
	 * A `GET /draft` failed while a copy was already in the store, so what is on
	 * screen may be behind what the service holds.
	 *
	 * Said rather than enforced. `fetchErrors` belongs to the whole slice and
	 * carries no procedure, so it cannot answer "did the read for *this*
	 * procedure fail" — a lock built on it either held a procedure whose own
	 * read was fine, or lifted off a stale one because a different procedure
	 * saved. A banner is true in both cases.
	 */
	refreshFailed: boolean;
	/** Raised by the publish attempt; the service decides, not the last read. */
	isBlocked: boolean;
	onCloseBlocked: () => void;
	onAcceptSuggestion: (suggestionId: string) => void;
	isPublishing: boolean;
	isSaving: boolean;
	/** A plan edit waiting out the pause, or in flight. */
	isSavePending: boolean;
	/** Why the last save did not land, while the edit is still on screen. */
	saveError: string | null;
	scenario: AOPScenario | null;
	scenariosHref: string;
	hrefFor: (version: number | null) => string;
	onSelectVersion: (version: number | null) => void;
	onChangePlan: (plan: AOPDraftPlan) => void;
	onPublish: () => void;
	onEdit: () => void;
}

/**
 * Everything inside the React Flow provider.
 */
export const AOPProcedureBody: FC<AOPProcedureBodyProps> = ({
	draft,
	shownDraft,
	summaries,
	versionCount,
	isLoadingMoreVersions,
	onLoadMoreVersions,
	selectedVersion,
	publishedVersion,
	hasUnpublishedChanges,
	isReadOnly,
	refreshFailed,
	isBlocked,
	onCloseBlocked,
	onAcceptSuggestion,
	isPublishing,
	isSaving,
	isSavePending,
	saveError,
	scenario,
	scenariosHref,
	hrefFor,
	onSelectVersion,
	onChangePlan,
	onPublish,
	onEdit,
}) => {
	const [fillRef, fillHeight] = useFillViewportHeight(480);
	const [isFullscreen, setIsFullscreen] = useState(false);

	const [focusStepId, setFocusStepId] = useState<string | null>(null);
	// antd portals a dropdown to `document.body`, which is outside a fixed
	// overlay's stacking and event context — the version picker's list would open
	// behind the canvas and swallow its own clicks. Scoped to the overlay while
	// it is up, the same fix the service-call calendar and the locations map use.
	const overlayRef = useRef<HTMLDivElement | null>(null);
	const popupContainer = useMemo(
		() => (isFullscreen ? () => overlayRef.current || document.body : undefined),
		[isFullscreen]
	);

	const mode = versionModeOf(selectedVersion, publishedVersion);

	/*
	  One answer for the strip, in the order that matters.

	  A failed read outranks a failed save: it says the document itself may not
	  be the current one, which is the larger of the two problems. A version is
	  read-only and saves nothing, so neither applies while one is open.
	*/
	const saveStatus: AOPSaveStatus = refreshFailed
		? 'stale'
		: isReadOnly
		? null
		: saveError
		? 'failed'
		: isSaving || isSavePending
		? 'saving'
		: null;
	const chrome = AOP_VERSION_CHROME[mode];
	// The service decides this: no suggestion still open and no violation. We no
	// longer count anything ourselves.
	// What is holding a publish up, by block. Read from the same payload the
	// service decided `publishable` from — nothing here re-derives that.
	const blockedItems = useMemo(
		() =>
			blockedItemsOf(
				nullSafeGet('plan', shownDraft),
				nullSafeGetOrElse('suggestions', shownDraft, []),
				nullSafeGetOrElse('violations', shownDraft, [])
			),
		[shownDraft]
	);

	return (
		<div
			ref={(node) => {
				overlayRef.current = node;
				fillRef(node);
			}}
			className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-[1000] bg-grey-1' : ''}`}
			// Measured height while the canvas sits in the page; the overlay is the
			// window, so it takes all of it.
			style={isFullscreen ? undefined : { height: fillHeight }}
		>
			<AOPVersionBar
				mode={mode}
				selectedVersion={selectedVersion}
				publishedVersion={publishedVersion}
				summaries={summaries}
				versionCount={versionCount}
				isLoadingMoreVersions={isLoadingMoreVersions}
				onLoadMoreVersions={onLoadMoreVersions}
				hasUnpublishedChanges={hasUnpublishedChanges}
				hrefFor={hrefFor}
				// The draft's own last-edited line. A draft is not a version, so this
				// comes off the procedure rather than from the version list.
				draftUpdatedAt={nullSafeGet('procedure.updated_at', draft)}
				draftUpdatedBy={nullSafeGet('procedure.updated_by', draft)}
				onSelectVersion={onSelectVersion}
				saveStatus={saveStatus}
				isFullscreen={isFullscreen}
				onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
				popupContainer={popupContainer}
			/>

			<div
				className={`relative flex-1 border-0 border-t-[3px] border-solid ${chrome.border}`}
				style={{ minHeight: 0 }}
			>
				{/*
				  The pill sits over the canvas rather than in the bar, so it reads as
				  a label on the document itself. Pointer events are handed back by the
				  pill so the rest of the strip does not block panning.
				*/}
				<div className="pointer-events-none absolute left-0 right-0 top-0 z-10 flex justify-center">
					<AOPVersionPill
						mode={mode}
						selectedVersion={selectedVersion}
						publishedVersion={publishedVersion}
						hasUnpublishedChanges={hasUnpublishedChanges}
						/*
						  Held while the service does not have the author's latest plan.

						  Publishing cuts a version from what the service holds, so one
						  cut before the last edit arrived would be missing it and say
						  it succeeded. Waiting for the save rather than racing it is
						  what this replaces: the save is a pause and a request, so the
						  control comes back in under a second, with the spinner beside
						  it saying why.

						  A failed save holds it open-endedly, which is the honest
						  answer — the plan on screen is not the one a version would be
						  cut from, and nothing here can fix that but another save.
						*/
						writeBlockedReason={
							saveError
								? 'Your last change has not been saved, so this cannot be published yet.'
								: isSavePending
								? 'Saving your last change…'
								: null
						}
						isPublishing={isPublishing}
						/*
						  Whether the draft can be published is the service's answer, not
						  one read from the draft in hand: it refuses with
						  `draft_not_publishable`, and that is what raises the modal.
						*/
						onPublish={onPublish}
						onEdit={onEdit}
					/>
				</div>

				<AOPBuilderCanvas
					key={`${shownDraft.procedure.id}:${selectedVersion}`}
					draft={shownDraft}
					onChangePlan={onChangePlan}
					onAcceptSuggestion={onAcceptSuggestion}
					isReadOnly={isReadOnly}
					scenario={scenario}
					scenariosHref={scenariosHref}
					focusStepId={focusStepId}
					onFocusHandled={() => setFocusStepId(null)}
				/>
			</div>

			{isBlocked ? (
				<AOPPublishBlockedModal
					items={blockedItems}
					onGoToStep={(stepId) => {
						onCloseBlocked();
						setFocusStepId(stepId);
					}}
					onClose={onCloseBlocked}
				/>
			) : null}
		</div>
	);
};
